import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { MaskLines, Reveal, Stagger, Item } from "../components/Motion";
import { Btn, Container, Eyebrow } from "../components/Shared";
import { UploadCta, PostCard } from "../components/home/SectionsB";
import { WA, PHONE } from "../components/Footer";
import { useSeo } from "../lib/seo";
import { useLang, fmtDate } from "../lib/lang";

const INTERP = ["interpreting", "consecutive", "online", "events"];
const SERVICE_KEYS = ["translation", "certified", "interpreting", "consecutive", "online", "events", "writing", "other"];

export const PageHero = ({ eyebrow, title, lede, meta, crumbs }) => (
  <section className="relative overflow-hidden pb-14 pt-36 md:pb-20 md:pt-44">
    <div className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,rgba(18,32,43,0.05)_1px,transparent_1px)] [background-size:calc(100%/6)_100%] md:[background-size:calc(100%/12)_100%]" />
    <Container className="relative">
      {crumbs && <motion.nav initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-wider text-[var(--muted)]" aria-label="Breadcrumb">{crumbs}</motion.nav>}
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="mt-6 max-w-5xl font-display text-[2.4rem] font-light leading-[1.02] tracking-[-0.03em] text-[var(--ink)] sm:text-5xl lg:text-[4.4rem]" data-testid="page-title">
        <MaskLines lines={[title]} />
      </h1>
      {lede && <Reveal delay={0.4} className="mt-8 max-w-2xl text-base text-[var(--muted)] md:text-lg">{lede}</Reveal>}
      {meta && <Reveal delay={0.5} className="mt-6 font-mono text-xs uppercase tracking-wider text-[var(--muted)]">{meta}</Reveal>}
    </Container>
  </section>
);

const Aside = ({ doc }) => {
  const { t, r } = useLang();
  const interp = INTERP.includes(doc.group);
  return (
    <aside className="space-y-6 lg:sticky lg:top-28">
      <div className="relative overflow-hidden rounded-sm bg-[var(--ink)] p-8 text-white grain">
        <h2 className="font-display text-2xl font-light">{interp ? t.page.contactTitle : t.page.quoteTitle}</h2>
        <p className="mt-3 text-white/75">{interp ? t.page.contactText : t.page.quoteText}</p>
        <Btn to={interp ? r("contact") : r("upload")} variant="teal" className="mt-6 w-full" data-testid="aside-cta-btn">{interp ? t.page.contactBtn : t.page.quoteBtn}</Btn>
      </div>
      <div className="rounded-sm border border-[var(--line)] bg-white p-7">
        <p className="eyebrow text-[var(--muted)]">{t.page.reach}</p>
        <ul className="mt-4 space-y-3 text-[var(--ink)]">
          <li><a href="mailto:info@translinked.net" className="flex items-center gap-3 hover:text-[var(--teal-ink)]" data-testid="aside-email"><Mail className="h-4 w-4 text-[var(--teal-ink)]" />info@translinked.net</a></li>
          <li><a href="tel:+491608493791" className="flex items-center gap-3 hover:text-[var(--teal-ink)]"><Phone className="h-4 w-4 text-[var(--teal-ink)]" />{PHONE}</a></li>
          <li><a href={WA} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[var(--teal-ink)]" data-testid="aside-whatsapp"><MessageCircle className="h-4 w-4 text-[var(--teal-ink)]" />WhatsApp</a></li>
        </ul>
      </div>
      {doc.type === "page" && doc.template !== "legal" && (
        <div className="rounded-sm border border-[var(--line)] p-7">
          <p className="eyebrow text-[var(--muted)]">{t.page.moreServices}</p>
          <ul className="mt-4">
            {SERVICE_KEYS.filter((k) => r(k) !== doc.path).map((k) => <li key={k}><Link to={r(k)} className="flex items-center justify-between border-t border-[var(--line)] py-2.5 text-sm text-[var(--ink)] hover:text-[var(--teal-ink)]">{t.nav[k]}<span>→</span></Link></li>)}
          </ul>
        </div>
      )}
    </aside>
  );
};

export default function ContentPage({ doc, alternates, related }) {
  const { t, r, lang } = useLang();
  useSeo({ title: doc.meta_title || `${doc.title} – Translinked`, description: doc.meta_description || doc.excerpt, path: doc.path, lang: doc.lang, alternates, image: doc.featured_image || undefined });
  const isPost = doc.type === "post";
  const words = (doc.content_html || "").replace(/<[^>]+>/g, " ").split(/\s+/).length;
  const eyebrow = isPost ? (doc.categories?.[0] || t.nav.blog) : { certified: t.nav.certified, service: t.nav.services, legal: t.footer.legal, about: t.nav.about, testimonials: t.footer.testimonials }[doc.template] || "Translinked";
  const crumbs = (
    <>
      <Link to={r("home")} className="hover:text-[var(--ink)]">{t.page.home}</Link><span>/</span>
      {isPost && <><Link to={r("blog")} className="hover:text-[var(--ink)]">{t.nav.blog}</Link><span>/</span></>}
      <span className="text-[var(--ink)] line-clamp-1">{doc.title}</span>
    </>
  );
  return (
    <main data-testid="content-page">
      <PageHero crumbs={crumbs} eyebrow={eyebrow} title={doc.title} lede={!isPost && doc.template !== "legal" ? doc.meta_description : ""} meta={isPost ? `${fmtDate(doc.date, lang)} · ${Math.max(1, Math.round(words / 220))} ${t.blog.min}` : ""} />
      {isPost && doc.featured_image && (
        <Container><Reveal className="overflow-hidden rounded-sm"><img src={doc.featured_image} alt={doc.title} className="max-h-[560px] w-full object-cover [clip-path:polygon(0_0,100%_0,100%_88%,94%_100%,0_100%)]" /></Reveal></Container>
      )}
      <Container className="grid gap-14 py-14 md:py-20 lg:grid-cols-12">
        <article className="lg:col-span-8" lang={doc.lang}>
          <Reveal y={16}><div className="prose-tl" data-testid="content-body" dangerouslySetInnerHTML={{ __html: doc.content_html }} /></Reveal>
          {isPost && <div className="mt-14"><Btn to={r("blog")} variant="ghost" data-testid="back-to-blog">{t.page.back}</Btn></div>}
        </article>
        <div className="lg:col-span-4"><Aside doc={doc} /></div>
      </Container>
      {isPost && related.length > 0 && (
        <section className="border-t border-[var(--line)] py-20">
          <Container>
            <p className="font-display text-3xl text-[var(--ink)]">{t.page.related}</p>
            <Stagger className="mt-10 grid gap-10 md:grid-cols-3 md:gap-6">{related.map((p) => <Item key={p.path}><PostCard p={p} lang={lang} t={t} /></Item>)}</Stagger>
          </Container>
        </section>
      )}
      {doc.template !== "legal" && <UploadCta />}
    </main>
  );
}
